"use client"

import { motion, AnimatePresence } from "framer-motion"
import { useState } from "react"
import { usePathname } from "next/navigation"
import { Section } from "@/components/layout/Section"
import { Card } from "@/components/ui/card"
import { Code, Smartphone, ShoppingCart, Zap, Palette, Globe, Cpu, Bot, Monitor, Store, PenTool } from "lucide-react"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { CodeAnimation } from "@/components/ui/CodeAnimation"
import { CTA } from "@/components/sections/CTA"
import { type Locale } from "@/lib/i18n"

const allServices = [
  {
    title: "Web Tasarım",
    description: "Modern, kullanıcı odaklı ve SEO uyumlu web tasarımları. Markanızın dijital kimliğini yansıtan, dönüşüm oranlarını artıran profesyonel web siteleri tasarlıyoruz.",
    icon: Palette,
    href: "/hizmetler/web-tasarim",
    code: "const design = createModernUI();",
  },
  {
    title: "Web Yazılım",
    description: "İşletmenize özel web yazılımları: yönetim panelleri, müşteri portalları ve entegrasyonlar. Yüksek performanslı, güvenli ve bakımı kolay çözümler geliştiriyoruz.",
    icon: Code,
    href: "/hizmetler/web-yazilim",
    code: "function buildApp() { return <App />; }",
  },
  {
    title: "UI & UX Tasarım",
    description: "Kullanıcı araştırması, wireframe, prototip ve arayüz tasarımı. Web ve mobil ürünleriniz için kolay kullanılan, dönüşüm odaklı dijital deneyimler tasarlıyoruz.",
    icon: PenTool,
    href: "/hizmetler/ui-ux-tasarim",
    code: "const flow = design.prototype(user);",
  },
  {
    title: "Mobil Uygulama",
    description: "iOS ve Android için native ve cross-platform mobil uygulamalar. Kullanıcı deneyimini ön planda tutan, performanslı mobil çözümler sunuyoruz.",
    icon: Smartphone,
    href: "/hizmetler/mobil-uygulama",
    code: "const app = new MobileApp();",
  },
  {
    title: "Masaüstü Uygulama",
    description: "Windows, macOS ve Linux için modern masaüstü uygulamaları. Electron ve native teknolojilerle platform bağımsız çözümler geliştiriyoruz.",
    icon: Monitor,
    href: "/hizmetler/masaustu-uygulama",
    code: "const desktop = new DesktopApp();",
  },
  {
    title: "E-ticaret Sistemleri",
    description: "Shopify, WooCommerce ve özel e-ticaret çözümleri. Satışlarınızı artıran, kullanıcı dostu ve güvenli online mağazalar kuruyoruz.",
    icon: ShoppingCart,
    href: "/hizmetler/e-ticaret",
    code: "shopify.init({ store: 'yours' });",
  },
  {
    title: "Shopify Tasarım - Geliştirme",
    description: "Shopify mağazanız için özel tema tasarımı ve geliştirme. Dönüşüm oranlarını artıran, mobil uyumlu ve SEO optimize edilmiş Shopify çözümleri.",
    icon: Store,
    href: "/hizmetler/shopify",
    code: "shopify.theme.create({ modern: true });",
  },
  {
    title: "WooCommerce Tasarım - Geliştirme",
    description: "WooCommerce mağazanız için özel tema ve eklenti geliştirme. WordPress ile entegre, özelleştirilebilir ve güçlü e-ticaret çözümleri.",
    icon: Store,
    href: "/hizmetler/woocommerce",
    code: "woocommerce.init({ custom: true });",
  },
  {
    title: "Otomasyon",
    description: "Tekrarlayan iş süreçlerinizi otomatikleştiren akıllı çözümler. Sistemlerinizi birbirine bağlayan, zaman ve maliyet tasarrufu sağlayan otomasyonlar kuruyoruz.",
    icon: Zap,
    href: "/hizmetler/otomasyon",
    code: "workflow.trigger('automation');",
  },
  {
    title: "AI Ürünleştirilmiş Servisler",
    description: "Yapay zeka teknolojileri ile fotoğraf, video ve metin üretimi. İşletmenizin ihtiyaçlarına özel AI entegrasyonları ve ürünleştirilmiş servisler sunuyoruz.",
    icon: Bot,
    href: "/hizmetler/ai-servisler",
    code: "ai.generate({ type: 'content' });",
  },
]

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
}

export default function ServicesPage() {
  const pathname = usePathname()
  const currentLocale: Locale = pathname?.startsWith('/en/') ? 'en' : 'tr'
  
  return (
    <>
      <Section className="bg-gradient-to-br from-[rgb(3,74,166)] to-[rgb(2,40,89)] text-white py-12 sm:py-16 lg:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl mb-6">
            Hizmetlerimiz
          </h1>
          <p className="text-lg text-white/90 sm:text-xl">
            Dijital dönüşüm yolculuğunuzda size eşlik eden kapsamlı hizmetlerimiz
          </p>
        </div>
      </Section>

      <Section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8"
          >
            {allServices.map((service) => {
              const Icon = service.icon
              const [isHovered, setIsHovered] = useState(false)
              
              return (
                <motion.div key={service.title} variants={itemVariants}>
                  <Link 
                    href={service.href} 
                    className="group block h-full"
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <Card className="h-full p-6 lg:p-8 border border-slate-200 hover:border-slate-300 hover:shadow-xl transition-all duration-300 rounded-none">
                      {/* Icon / Code Animation Row */}
                      <div className="mb-6 min-h-[60px] flex items-start">
                        <AnimatePresence mode="wait">
                          {!isHovered ? (
                            <motion.div
                              key="icon"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="inline-flex p-4 bg-slate-100 group-hover:bg-[rgb(3,74,166)] transition-colors duration-300"
                            >
                              <Icon className="h-6 w-6 text-slate-700 group-hover:text-white transition-colors duration-300" />
                            </motion.div>
                          ) : (
                            <motion.div
                              key="code"
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -10 }}
                              transition={{ duration: 0.2 }}
                              className="w-full"
                            >
                              <CodeAnimation code={service.code} isVisible={isHovered} />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                      
                      <h3 className="text-xl lg:text-2xl font-bold text-slate-900 mb-3 lg:mb-4 group-hover:text-[rgb(3,74,166)] transition-colors duration-300">
                        {service.title}
                      </h3>
                      
                      <p className="text-sm lg:text-base text-slate-600 leading-relaxed mb-4 lg:mb-6">
                        {service.description}
                      </p>
                      
                      <div className="flex items-center text-[rgb(3,74,166)] font-medium mt-auto pt-3 lg:pt-4 border-t border-slate-100 group-hover:border-slate-200 transition-colors">
                        <span className="text-xs lg:text-sm">Daha Fazla Bilgi</span>
                        <ArrowRight className="h-3 w-3 lg:h-4 lg:w-4 ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </Card>
                  </Link>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </Section>
      <CTA locale={currentLocale} />
    </>
  )
}

